import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react';
import { processPayment } from '../../../services/userService';
import NoData from '../../../components/common/NoData';

// Helper function to get currency symbol
const getCurrencySymbol = (currency) => {
  switch (currency?.toUpperCase()) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'SGD':
      return 'S$';
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    default:
      return '$'; // Default to USD symbol
  }
};

const razorpayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;

function OrderDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const courseData = location.state;

  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  useEffect(() => {
    // Validate and log the received data
    // console.log('OrderDetails - Received State:', location.state);
    
    if (!location.state || !location.state.course_id) {
      console.error('No course data or course ID found');
      // Optionally redirect back or show error
      // navigate(-1);
    }

    

    // Log all the course data
    // Commented multi-line console.log);
  }, [location.state]);

  const subtotal = Number(courseData?.course_price) || 0;
  const discount = Number(courseData?.discountValue) || 0;
  const total = Math.max(0, subtotal - discount);

  const handleCheckout = async () => {
    if (total <= 0) {
      toast.error('Total must be greater than 0');
      return;
    }

    if (!razorpayKeyId) {
      toast.error('Razorpay configuration is missing');
      return;
    }

    setIsCheckoutLoading(true);
    try {
      // Create order in backend
      const response = await processPayment({
        amount: total * 100, // in paise (smallest unit)
        currency: courseData.currency || 'INR',
        origin: window.location.origin,
        courseName: courseData.course_name,
        points: courseData.points,
        course_id: courseData.course_id,
      });

      if (!response.success) {
        toast.error(response.error_msg || 'Failed to create payment order');
        setIsCheckoutLoading(false);
        return;
      }

      // Initialize Razorpay
      const options = {
        key: razorpayKeyId,
        amount: total * 100, // in paise
        currency: courseData.currency || 'INR',
        name: courseData.course_name,
        description: `Purchase course: ${courseData.course_name}`,
        order_id: response.razorpay_order_id,
        handler: async (paymentResponse) => {
          try {
            // Verify payment with backend
            const verifyResponse = await processPayment({
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_signature: paymentResponse.razorpay_signature,
              course_id: courseData.course_id,
            });

            if (verifyResponse.success) {
              toast.success('Payment successful! Course added to your account.');
              navigate('/user/my-courses', { replace: true });
            } else {
              toast.error('Payment verification failed');
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            toast.error('Payment verification failed');
          } finally {
            setIsCheckoutLoading(false);
          }
        },
        prefill: {
          name: courseData.student_name || 'Student',
          email: courseData.student_email || '',
          contact: courseData.student_phone || '',
        },
        theme: {
          color: '#007bff',
        },
        modal: {
          ondismiss: () => {
            setIsCheckoutLoading(false);
            toast.info('Payment cancelled');
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Checkout Error:', error);
      toast.error('Failed to initiate payment');
      setIsCheckoutLoading(false);
    }
  };

  if (!courseData || !courseData.course_id) {
    return (
      <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <div className="mb-4">
            <Icon icon="fluent:shopping-cart-24-regular" className="text-gray-500" style={{ fontSize: '4rem' }} />
          </div>
          <h3 className="text-gray-500 mb-3">No order data available</h3>
          <p className="text-gray-500 mb-4">Please select a course to purchase</p>
          <button 
            className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-4 rounded-lg"
            onClick={() => navigate(-1)}
          >
            <Icon icon="fluent:arrow-left-24-regular" className="mr-2" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="flex justify-center">
        <div className="w-full lg:w-8/12">
          {/* Header */}
          <div className="text-left mb-3">
            <h1 className="text-4xl font-bold text-dark mb-2">Order Details</h1>
            <div className="flex justify-left items-center">
              <div className="bg-primary rounded-full px-3 py-1">
                <small className="text-white font-medium">
                  <Icon icon="fluent:lock-24-regular" className="mr-1" />
                  Secure Payment Gateway
                </small>
              </div>
            </div>
          </div>

          {/* Single Card Design */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100" style={{ borderRadius: '20px' }}>
            <div className="p-4">
              {/* Course Information Section */}
              <div className="flex mb-5">
                <div className="w-full md:w-4/12 text-center">
                  <div className="relative mb-3">
                    <img
                      src={courseData.banner_image}
                      alt={courseData.course_name}
                      className="w-full rounded-lg"
                      style={{ 
                        height: '200px', 
                        width: '100%', 
                        objectFit: 'cover',
                        boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/placeholder-course-image.jpg';
                      }}
                    />
                    <div className="absolute top-0 right-0 m-2">
                      <span className="bg-primary px-2 py-1 rounded-full text-white text-sm">
                        <Icon icon="fluent:graduation-cap-24-regular" className="mr-1" />
                        {courseData.course_type}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-8/12">
                  <h3 className="font-bold text-dark mb-3">{courseData.course_name}</h3>
                  <p className="text-gray-500 mb-4" style={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}>{courseData.course_desc}</p>
                </div>
              </div>

              {/* Price Information */}
              <div className="flex mb-4">
                <div className="w-1/2">
                  <div className="bg-gray-100 rounded-lg p-3 text-center">
                    <small className="text-gray-500 block">Course Price</small>
                    <span className="text-xl font-bold text-primary mb-0">{getCurrencySymbol(courseData?.currency)}{subtotal.toFixed(2)}</span>
                  </div>
                </div>
                <div className="w-1/2">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <small className="text-gray-500 block">Final Price</small>
                    <span className="text-xl font-bold text-green-600 mb-0">{getCurrencySymbol(courseData?.currency)}{total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              {/* Payment Summary Section */}
              <div className="mb-4">
                <div className="flex items-center mb-4">
                  <div className="bg-primary/10 rounded-full p-1 mr-3 border border-primary">
                    <Icon icon="mdi:credit-card" className="text-xl text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-dark mb-1">Payment Summary</h4>
                    <p className="text-gray-500 mb-0">Complete your purchase securely</p>
                  </div>
                </div>

                <div className="payment-breakdown mb-4">
                  <div className="flex justify-between items-center py-3 border-b border-gray-200">
                    <div className="flex items-center">
                      <Icon icon="fluent:book-24-regular" className="text-primary mr-2" />
                      <span className="font-medium">Course Price</span>
                    </div>
                    <span className="font-bold">{getCurrencySymbol(courseData?.currency)}{subtotal.toFixed(2)}</span>
                  </div>

                  {discount > 0 && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-200 text-green-600">
                      <div className="flex items-center">
                        <Icon icon="fluent:tag-24-regular" className="text-green-600 mr-2" />
                        <span className="font-medium">Discount ({courseData.discountCode})</span>
                      </div>
                      <span className="font-bold">-{getCurrencySymbol(courseData?.currency)}{discount.toFixed(2)}</span>
                    </div>
                  )}

                  {courseData.points > 0 && (
                    <div className="flex justify-between items-center py-3 border-b border-gray-200 text-blue-500">
                      <div className="flex items-center">
                        <Icon icon="fluent:coins-24-regular" className="text-blue-500 mr-2" />
                        <span className="font-medium">Points Used</span>
                      </div>
                      <span className="font-bold">{courseData.points} points</span>
                    </div>
                  )}
                </div>

                <div className="payment-actions">
                  <button
                    className="bg-primary hover:bg-primary/90 text-white font-medium py-3 rounded-full shadow-sm w-full text-lg font-semibold"
                    onClick={handleCheckout}
                    disabled={isCheckoutLoading || total <= 0}
                    style={{ 
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                      border: 'none'
                    }}
                  >
                    {isCheckoutLoading ? (
                      <>
                        <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></span>
                        Processing Payment...
                      </>
                    ) : (
                      <>
                        <Icon icon="fluent:lock-24-regular" className="mr-2" />
                        Pay {getCurrencySymbol(courseData?.currency)}{total.toFixed(2)} Securely
                      </>
                    )}
                  </button>

                  <div className="text-center mt-3">
                    <small className="text-gray-500">
                      <Icon icon="fluent:shield-24-regular" className="mr-1" />
                      Your payment information is secure and encrypted
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderDetails;
